import React, { useState, useEffect, useRef } from 'react';
import CustomScrollTrigger from 'js/wrapper/CustomScrollTrigger';
import { Parallax } from 'react-scroll-parallax';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useAbstractCmsElement } from 'components/cms/hooks/useAbstractCmsElementNew';


const CmsElementAnimatedHeader = (props) => {
  const { editMode, index, showElementSettings } = props;

  // CMS Logic Hook
  const {
    shown,
    setShown,
    getMainId,
    getCustomId,
    getParamValue,
    getBackgroundStyle,
    elementStyleTag,
    getParallaxData,
    getAppendHtml,
    cssClasses,
    responsiveClasses
  } = useAbstractCmsElement(props);

  // Animation State
  const [animationActive, setAnimationActive] = useState(false);
  const [optionIndex, setOptionIndex] = useState(0);
  const [optionProgress, setOptionProgress] = useState(0);
  const [blinkActive, setBlinkActive] = useState(false);

  const animTimeoutRef = useRef(null);

  // dnd-kit Sortable
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: index,
    disabled: !editMode
  });

  const dndStyle = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
    position: 'relative',
    width: '100%',
    cursor: editMode ? 'move' : 'default'
  };

  // Animation Logic
  const options = getParamValue('options', 'Header|Values|Text').split('|');

  useEffect(() => {
    if (!animationActive) return;

    const currentWord = options[optionIndex] || '';
    let delay = 200;

    const tick = () => {
      if (blinkActive) {
        // Pause am Ende des Wortes vorbei -> nächstes Wort
        setBlinkActive(false);
        setOptionIndex((prev) => (prev + 1) % options.length);
        setOptionProgress(0);
      } else {
        if (optionProgress >= currentWord.length) {
          // Wort fertig getippt -> Blinken starten
          setBlinkActive(true);
          // delay für das Blinken am Ende
        } else {
          // Nächsten Buchstaben tippen
          setOptionProgress((prev) => prev + 1);
        }
      }
    };

    if (blinkActive) delay = 600;

    animTimeoutRef.current = setTimeout(tick, delay);

    return () => clearTimeout(animTimeoutRef.current);
  }, [animationActive, optionIndex, optionProgress, blinkActive, options.length]);

  // Handlers
  const handleOnEnter = () => {
    setShown(true);
    setAnimationActive(true);
  };

  const handleOnExit = () => {
    setAnimationActive(false);
    setOptionIndex(0);
    setOptionProgress(0);
    setBlinkActive(false);
  };

  // Render Helpers
  const HeaderTag = `h${Math.max(1, Math.min(6, parseInt(getParamValue('headerSize', '1'), 10)))}`;
  const title = getParamValue('title', 'Animated');
  const mainColorClass = `text-${getParamValue('mainColor', 'master')}`;
  const displayedOption = options[optionIndex] ? options[optionIndex].substring(0, optionProgress) : '';

  const innerContent = (
    <div
      id={getMainId()}
      className={`ms-animated-header relative ${shown ? 'elem-shown' : 'elem-hidden'} ${mainColorClass} ${cssClasses} ${responsiveClasses}`.trim()}
      onClick={(e) => editMode && showElementSettings(e, index)}
      style={getBackgroundStyle()}
    >
      {elementStyleTag}

      <HeaderTag>
        {title} <span className="typed">{displayedOption}</span>
        <span className={`typed-cursor ${blinkActive ? 'typed-cursor--blink' : ''}`}>|</span>
      </HeaderTag>

      {getAppendHtml()}
    </div>
  );

  const parallaxData = getParallaxData();

  return (
    <div ref={setNodeRef} style={dndStyle} {...attributes} {...listeners}>
      <div>
        <CustomScrollTrigger
          className="cms-overflow-fix"
          onEnter={handleOnEnter}
          onExit={handleOnExit}
          {...getCustomId()}
        >
          {parallaxData ? (
            <Parallax {...parallaxData}>{innerContent}</Parallax>
          ) : (
            innerContent
          )}
        </CustomScrollTrigger>
      </div>
    </div>
  );
};

export default CmsElementAnimatedHeader;
